// ═══════════════════════════════════════════════════════════════
// LogsDashboard — Audit trail & evenements systeme ITRIIA
// Session 32 — Admin Logs fonctionnel
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Shield, AlertTriangle, LogIn, DollarSign, Filter, ChevronDown } from 'lucide-react';

const formatCost = (usd) => {
  if (!usd || usd === 0) return '';
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
};

const timeAgo = (ts) => {
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'A l\'instant';
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
  return `Il y a ${Math.floor(diff / 86400)}j`;
};

const serviceLabels = {
  openrouter_llm: { label: 'LLM', color: '#3b82f6', icon: '🤖' },
  openrouter: { label: 'LLM', color: '#3b82f6', icon: '🤖' },
  whisper: { label: 'Voice', color: '#f59e0b', icon: '🎙️' },
  tts: { label: 'Studio', color: '#8b5cf6', icon: '🎧' },
  embedding: { label: 'Embed', color: '#06b6d4', icon: '🧬' },
  supadata: { label: 'Supadata', color: '#10b981', icon: '📺' },
  auth: { label: 'Auth', color: '#3b82f6', icon: '🔐' },
};

const LogsDashboard = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [type, setType] = useState('all');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');

  const loadLogs = useCallback(async (reset = true) => {
    try {
      const token = localStorage.getItem('oracle_token');
      const currentPage = reset ? 0 : page;
      const params = new URLSearchParams({ days, type, page: currentPage });
      if (selectedUser) params.set('user', selectedUser);

      const res = await fetch(`/api/admin/logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (reset) {
          setLogs(data.logs);
          setPage(0);
        } else {
          setLogs(prev => [...prev, ...data.logs]);
        }
        setStats(data.stats);
        setHasMore(data.pagination.has_more);
      }
    } catch (e) {
      console.error('Erreur logs:', e);
    } finally {
      setLoading(false);
    }
  }, [days, type, page, selectedUser]);

  // Load users for filter
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const token = localStorage.getItem('oracle_token');
        const res = await fetch('/api/admin/users', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUsers(data.users || data);
        }
      } catch (e) { /* ignore */ }
    };
    loadUsers();
  }, []);

  useEffect(() => { loadLogs(true); }, [days, type, selectedUser]);

  const loadMore = () => {
    setPage(p => p + 1);
    setTimeout(() => loadLogs(false), 0);
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement...</div>;

  const cardStyle = {
    background: 'var(--card-bg)', border: '1px solid var(--card-border)',
    borderRadius: '12px', padding: '20px',
  };

  const statBox = (icon, label, value, color) => (
    <div style={{
      ...cardStyle, display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 20px',
    }}>
      <div style={{
        width: '38px', height: '38px', borderRadius: '10px',
        background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { size: 18, style: { color } })}
      </div>
      <div>
        <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '1px' }}>{label}</div>
        <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text)' }}>{value}</div>
      </div>
    </div>
  );

  const selectStyle = {
    padding: '6px 12px', borderRadius: '8px', fontSize: '12px',
    background: 'var(--input-bg)', color: 'var(--text)', border: '1px solid var(--card-border)',
    cursor: 'pointer', appearance: 'none', paddingRight: '28px',
  };

  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FileText size={22} style={{ color: '#3b82f6' }} />
            Logs & Audit
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Journal d'activite systeme
          </p>
        </div>
      </div>

      {/* Filters bar */}
      <div style={{
        ...cardStyle, padding: '12px 16px', marginBottom: '20px',
        display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
      }}>
        <Filter size={14} style={{ color: 'var(--text-secondary)' }} />

        {/* Type filter */}
        <div style={{ position: 'relative' }}>
          <select value={type} onChange={e => setType(e.target.value)} style={selectStyle}>
            <option value="all">Tous les types</option>
            <option value="usage">Utilisation LLM</option>
            <option value="auth">Connexions</option>
            <option value="error">Erreurs</option>
          </select>
          <ChevronDown size={12} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none' }} />
        </div>

        {/* Period */}
        <div style={{ display: 'flex', gap: '4px' }}>
          {[1, 3, 7].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '500',
              cursor: 'pointer', border: 'none', transition: 'all 0.2s',
              background: days === d ? 'linear-gradient(135deg, #2563eb, #1d4ed8)' : 'var(--input-bg)',
              color: days === d ? 'white' : 'var(--text-secondary)',
            }}>
              {d === 1 ? '24h' : `${d}j`}
            </button>
          ))}
        </div>

        {/* User filter */}
        {users.length > 0 && (
          <div style={{ position: 'relative' }}>
            <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)} style={selectStyle}>
              <option value="">Tous les utilisateurs</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
              ))}
            </select>
            <ChevronDown size={12} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none' }} />
          </div>
        )}
      </div>

      {/* Stats cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          {statBox(<FileText />, `Evenements (${days === 1 ? '24h' : days + 'j'})`, stats.total_events.toLocaleString(), '#3b82f6')}
          {statBox(<AlertTriangle />, 'Erreurs', stats.errors.toLocaleString(), '#ef4444')}
          {statBox(<LogIn />, 'Connexions', stats.logins.toLocaleString(), '#10b981')}
          {statBox(<DollarSign />, 'Cout total', `$${stats.total_cost.toFixed(2)}`, '#f59e0b')}
        </div>
      )}

      {/* Logs table */}
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                {['Horodatage', 'Utilisateur', 'Type', 'Action', 'Modele', 'Cout', 'Statut'].map(h => (
                  <th key={h} style={{
                    padding: '12px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '600',
                    color: 'var(--text-secondary)', background: 'var(--input-bg)', whiteSpace: 'nowrap',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => {
                const svc = serviceLabels[log.service_type] || { label: log.service_type, color: '#6b7280', icon: '⚡' };
                const isError = log.status === 'error';
                const isLogin = log.source === 'login';
                return (
                  <tr key={`${log.source}-${log.id}-${i}`} style={{
                    borderBottom: '1px solid var(--card-border)',
                    background: isError ? 'rgba(239,68,68,0.04)' : isLogin ? 'rgba(59,130,246,0.04)' : 'transparent',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { if (!isError && !isLogin) e.currentTarget.style.background = 'var(--input-bg)'; }}
                  onMouseLeave={e => { if (!isError && !isLogin) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}
                        title={new Date(log.timestamp).toLocaleString('fr-FR')}>
                      {timeAgo(log.timestamp)}
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: '500', color: 'var(--text)' }}>
                      {log.full_name || log.username}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                        background: `${svc.color}15`, color: svc.color,
                      }}>
                        {svc.icon} {svc.label}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--text)', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.action_label || log.action_type}
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', fontSize: '11px' }}>
                      {log.model ? log.model.split('/').pop() : '-'}
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: '600', color: log.cost_usd > 0 ? '#10b981' : 'var(--text-secondary)' }}>
                      {formatCost(log.cost_usd) || '-'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                        background: isError ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                        color: isError ? '#ef4444' : '#10b981',
                      }}>
                        {isError ? 'Erreur' : 'OK'}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    Aucun evenement pour cette periode
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Load more */}
        {hasMore && (
          <div style={{ padding: '12px', textAlign: 'center', borderTop: '1px solid var(--card-border)' }}>
            <button onClick={loadMore} style={{
              padding: '8px 24px', borderRadius: '8px', fontSize: '12px', fontWeight: '500',
              cursor: 'pointer', border: 'none',
              background: 'var(--input-bg)', color: 'var(--text-secondary)',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb, #1d4ed8)'}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--input-bg)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
            >
              Charger plus...
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogsDashboard;
